import api from './axios';

// Production Lot API services - SOAP-based SAP ByDesign Integration

/**
 * Get production lot by ID with barcode
 * @param {string} id - Production Lot ID (e.g., "14273") OR Production Order ID
 * @param {boolean} includeBarcode - Whether to include barcode image
 * @param {boolean} includeTaskBarcodes - Whether to include task-level barcodes
 * @param {boolean} switcher - true for Task ID lookup, false for Order ID lookup
 */
export const getProductionLotById = async (
  id,
  includeBarcode = true,
  includeTaskBarcodes = false,
  switcher = true
) => {
  const response = await api.get(
    `/api/production-lots/${encodeURIComponent(id)}`,
    {
      params: { includeBarcode, includeTaskBarcodes, switcher }
    }
  );
  return response.data;
};

/**
 * Get production lots by Order ID
 * @param {string} orderId - Production Order ID
 * @param {boolean} includeBarcode - Whether to include barcode images
 * @param {boolean} includeTaskBarcodes - Whether to include task-level barcodes
 */
export const getProductionLotsByOrderId = async (orderId, includeBarcode = true, includeTaskBarcodes = false) => {
  const response = await api.get(
    `/api/production-lots/by-order/${encodeURIComponent(orderId)}`,
    {
      params: { includeBarcode, includeTaskBarcodes }
    }
  );
  return response.data;
};

/**
 * Search production orders by Sales Order No (Product Specification ID).
 * Can return multiple results.
 * @param {string} specId - Sales Order No / Product Specification ID
 */
export const getProductionLotsBySpecificationId = async (specId) => {
  const response = await api.get(
    `/api/production-lots/by-specification/${encodeURIComponent(specId)}`
  );
  return response.data;
};

/**
 * Look up Sales Order No (Product Specification ID) for a given Production Order ID.
 * @param {string} productionOrderId - Production Order ID
 * @returns {{ isSuccess, data: string|null, message }}
 */
export const getSalesOrderNoByOrderId = async (productionOrderId) => {
  try {
    const response = await api.get(
      `/api/production-lots/sales-order-no/${encodeURIComponent(productionOrderId)}`
    );
    return response.data;
  } catch {
    return { isSuccess: false, data: null };
  }
};

/**
 * Get production lots by Site ID
 * @param {string} siteId - Site ID
 * @param {string} statusCodes - Comma-separated status codes
 * @param {boolean} includeBarcode - Whether to include barcode images
 */
export const getProductionLotsBySite = async (
  siteId,
  statusCodes = null,
  includeBarcode = true
) => {
  const response = await api.get(
    `/api/production-lots/by-site/${encodeURIComponent(siteId)}`,
    {
      params: { statusCodes, includeBarcode }
    }
  );
  return response.data;
};

/**
 * Query production lots with filters
 * @param {Object} request - Query request object
 */
export const queryProductionLots = async (request) => {
  const response = await api.post('/api/production-lots/query', request);
  return response.data;
};

/**
 * Generate barcode for any content
 * @param {string} content - Content to encode in barcode
 * @returns {string} - URL for the barcode image
 */
export const getBarcodeUrl = (content) => {
  const baseUrl = api.defaults.baseURL || '';
  return `${baseUrl}/api/production-lots/barcode/generate?content=${encodeURIComponent(content)}`;
};

/**
 * Get the barcode image URL for a production lot
 * @param {string} lotId - Production Lot ID
 * @returns {string} - URL for the barcode image
 */
export const getProductionLotBarcodeUrl = (lotId) => {
  const baseUrl = api.defaults.baseURL || '';
  return `${baseUrl}/api/production-lots/${encodeURIComponent(lotId)}/barcode`;
};

/**
 * Get status description from code
 * @param {string} statusCode - Status code (1-6)
 */
export const getStatusDescription = (statusCode) => {
  const statuses = {
    '1': 'In Preparation',
    '2': 'Released',
    '3': 'Started',
    '4': 'Finished',
    '5': 'Closed',
    '6': 'Cancelled'
  };
  return statuses[statusCode] || statusCode || 'Unknown';
};

/**
 * Get status color class based on code
 * @param {string} statusCode - Status code
 */
export const getStatusColorClass = (statusCode) => {
  const colors = {
    '1': 'bg-gray-100 text-gray-700',
    '2': 'bg-blue-100 text-blue-700',
    '3': 'bg-yellow-100 text-yellow-700',
    '4': 'bg-green-100 text-green-700',
    '5': 'bg-purple-100 text-purple-700',
    '6': 'bg-red-100 text-red-700'
  };
  return colors[statusCode] || 'bg-gray-100 text-gray-700';
};

export default {
  getProductionLotById,
  getProductionLotsByOrderId,
  getProductionLotsBySpecificationId,
  getProductionLotsBySite,
  queryProductionLots,
  getBarcodeUrl,
  getProductionLotBarcodeUrl,
  getStatusDescription,
  getStatusColorClass
};